'use client';

import { c as _c } from "react/compiler-runtime";
import { createContext, useContext, useEffect, useCallback, ReactNode, useRef } from 'react';
type ShortcutHandler = () => void;
interface KeyboardShortcut {
  key: string;
  metaKey?: boolean;
  ctrlKey?: boolean;
  shiftKey?: boolean;
  altKey?: boolean;
  handler: ShortcutHandler;
  description?: string;
}
interface KeyboardShortcutsContextValue {
  registerShortcut: (id: string, shortcut: KeyboardShortcut) => void;
  unregisterShortcut: (id: string) => void;
}
const KeyboardShortcutsContext = createContext<KeyboardShortcutsContextValue | null>(null);
export function useKeyboardShortcuts() {
  const context = useContext(KeyboardShortcutsContext);
  if (!context) {
    throw new Error("useKeyboardShortcuts must be used within KeyboardShortcutsProvider");
  }
  return context;
}
interface KeyboardShortcutsProviderProps {
  children: ReactNode;
}
export function KeyboardShortcutsProvider(t0) {
  const $ = _c(8);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Map();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const shortcutsRef = useRef(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = (id, shortcut) => {
      shortcutsRef.current.set(id, shortcut);
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const registerShortcut = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = id_0 => {
      shortcutsRef.current.delete(id_0);
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const unregisterShortcut = t3;
  let t4;
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      const handleKeyDown = event => {
        const target = event.target as HTMLElement;
        const isInputField = target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable;
        if (!event.key) {
          return;
        }
        for (const [, shortcut_0] of shortcutsRef.current) {
          const keyMatches = event.key.toLowerCase() === shortcut_0.key.toLowerCase();
          const metaMatches = shortcut_0.metaKey === undefined || event.metaKey === shortcut_0.metaKey;
          const ctrlMatches = shortcut_0.ctrlKey === undefined || event.ctrlKey === shortcut_0.ctrlKey;
          const shiftMatches = shortcut_0.shiftKey === undefined || event.shiftKey === shortcut_0.shiftKey;
          const altMatches = shortcut_0.altKey === undefined || event.altKey === shortcut_0.altKey;
          const isSearchShortcut = shortcut_0.key === "/" && (shortcut_0.metaKey || shortcut_0.ctrlKey);
          if (keyMatches && metaMatches && ctrlMatches && shiftMatches && altMatches) {
            if (!isInputField || isSearchShortcut) {
              event.preventDefault();
              shortcut_0.handler();
              break;
            }
          }
        }
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    };
    t5 = [];
    $[3] = t4;
    $[4] = t5;
  } else {
    t4 = $[3];
    t5 = $[4];
  }
  useEffect(t4, t5);
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      registerShortcut,
      unregisterShortcut
    };
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== children) {
    t7 = <KeyboardShortcutsContext.Provider value={t6}>{children}</KeyboardShortcutsContext.Provider>;
    $[6] = children;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  return t7;
}

// Hook to register a keyboard shortcut
export function useKeyboardShortcut(id, shortcut, handler, t0) {
  const $ = _c(13);
  const enabled = t0 === undefined ? true : t0;
  const {
    registerShortcut,
    unregisterShortcut
  } = useKeyboardShortcuts();
  let t1;
  if ($[0] !== enabled || $[1] !== handler || $[2] !== id || $[3] !== registerShortcut || $[4] !== shortcut || $[5] !== unregisterShortcut) {
    t1 = () => {
      if (enabled) {
        registerShortcut(id, {
          ...shortcut,
          handler
        });
        return () => unregisterShortcut(id);
      }
    };
    $[0] = enabled;
    $[1] = handler;
    $[2] = id;
    $[3] = registerShortcut;
    $[4] = shortcut;
    $[5] = unregisterShortcut;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== enabled || $[8] !== handler || $[9] !== id || $[10] !== registerShortcut || $[11] !== unregisterShortcut) {
    t2 = [id, enabled, handler, registerShortcut, unregisterShortcut];
    $[7] = enabled;
    $[8] = handler;
    $[9] = id;
    $[10] = registerShortcut;
    $[11] = unregisterShortcut;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  useEffect(t1, t2);
}